<template>
    <div class="full-width d-flex justify-center align-center flex-column">
        <v-progress-circular v-if="customProductInfoLoading" color="primary" indeterminate size="48" class="py-8" />
        <template v-else-if="customProductInfo">
            <v-img
                v-if="customProductInfo.imageUrl && !loadedImage"
                tile
                class="mb-2"
                :src="customProductInfo.imageUrl"
                :lazy-src="$filters.srcHost('/placeholder.png')"
                height="250"
                width="100%"
                :eager="true"
                @error="onImgError"
            >
                <template v-slot:placeholder>
                    <v-row
                        v-show="!imgError && customProductInfo.imageUrl"
                        class="fill-height ma-0"
                        align="center"
                        justify="center"
                    >
                        <v-progress-circular indeterminate color="grey-lighten-5" />
                    </v-row>
                </template>
            </v-img>
            <v-img
                v-if="loadedImage"
                tile
                class="mb-2"
                :src="loadedImage"
                :lazy-src="$filters.srcHost('/placeholder.png')"
                height="250"
                width="100%"
                :eager="true"
                @error="onImgError"
            />
            <v-file-input
                v-model="fileUploaded"
                prepend-icon="mdi-camera"
                small-chips
                variant="outlined"
                density="compact"
                accept="image/jpg, image/jpeg, image/png"
                class="cursor-pointer full-width"
                hide-details
                :label="$t('select_photo')"
                @update:model-value="onPhotoLoad"
            />
            <span> {{ $t('or') }} </span>
            <v-text-field
                v-model="customProductInfo.imageUrl"
                prepend-icon="mdi-pencil"
                :disabled="loadedImage"
                density="compact"
                class="full-width"
                variant="outlined"
                :label="$t('input_image_url')"
            />
            <v-textarea
                v-model="customProductInfoDescription"
                hide-details
                rows="8"
                density="compact"
                class="full-width mt-4"
                variant="outlined"
                :label="$t('description')"
            />
        </template>
        <v-card-actions class="full-width">
            <v-btn
                :disabled="customProductInfoLoading || customProductInfoSaveLoading"
                :loading="customProductInfoSaveLoading"
                color="primary"
                @click="saveCustomProductInfo"
            >
                {{ $t('save') }}
            </v-btn>
        </v-card-actions>
    </div>
</template>

<script lang="ts">
    import {Component, Emit, Prop, Vue} from 'vue-facing-decorator'
    import {imageUrl} from '@/utils/filters'
    import {blobToBase64} from '~/utils/helpers'

    @Component
    export class CustomProductInfoForm extends Vue {
        @Prop() service

        imgError = false
        customProductInfo = null
        customProductInfoLoading = false
        customProductInfoSaveLoading = false
        loadedImage = null

        fileUploaded = null

        @Emit()
        close() {}

        @Emit()
        closeModal() {}

        onImgError() {
            this.imgError = true
            return false
        }

        async mounted() {
            await this.loadCustomProductInfo()
        }

        async loadCustomProductInfo() {
            try {
                this.customProductInfoLoading = true

                this.customProductInfo = await this.$api.customProductInfo.get({
                    serviceId: this.service.serviceId,
                })
                this.customProductInfo.imageUrl = imageUrl(this.customProductInfo.imageUrl, 800)
                if (!this.customProductInfo.descriptions) {
                    this.customProductInfo.descriptions = [
                        {
                            value: '',
                            locale: 'en',
                        },
                    ]
                }
            } catch (e) {
                this.$toast.error(this.$t('error_message.load_error'))
                this.close()
            } finally {
                this.customProductInfoLoading = false
            }
        }

        async saveCustomProductInfo() {
            try {
                this.customProductInfoSaveLoading = true
                const request = {
                    descriptions: this.customProductInfo?.descriptions,
                    ...(this.loadedImage && {
                        fileNames: {
                            en:
                                'image' +
                                (this.fileUploaded.name.split('.').pop()
                                    ? `.${this.fileUploaded.name.split('.').pop()}`
                                    : ''),
                        },
                    }),
                    ...(this.loadedImage && {fileStream: this.loadedImage.split(',')[1]}),
                    ...(!this.loadedImage &&
                        this.customProductInfo.imageUrl && {imageUrl: this.customProductInfo.imageUrl}),
                }

                await this.$api.customProductInfo.put(this.service.serviceId, request)
                this.$toast.success(this.$t('success_message.saved_successfully'))
                this.loadedImage = null
                this.closeModal()
            } catch (e) {
                this.$toast.error(this.$t('error_message.save_error'))
            } finally {
                this.customProductInfoSaveLoading = false
            }
        }

        async onPhotoLoad() {
            if (this.fileUploaded) {
                this.loadedImage = await blobToBase64(this.fileUploaded)
            } else {
                this.loadedImage = null
            }
        }

        get customProductInfoDescription() {
            return this.customProductInfo?.descriptions?.find(d => d.locale === 'en')?.value || ''
        }

        set customProductInfoDescription(val) {
            let description = this.customProductInfo?.descriptions.find(d => d.locale === 'en')
            if (!description) {
                this.customProductInfo?.descriptions.push({
                    locale: 'en',
                    value: '',
                })
            }
            description = this.customProductInfo?.descriptions.find(d => d.locale === 'en')
            description.value = val
        }
    }

    export default CustomProductInfoForm
</script>
